import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { integrationsService } from '@/services/integrations'

export const CREDENTIAL_ERROR_COUNT_KEY = 'credentialErrorCount'

/**
 * Credential-error count for the sidebar badge.
 *
 * The dashboard layout renders the sidebar twice (desktop + mobile drawer), so
 * the previous fetch-in-effect fired one request per instance — the ×2 pair we
 * saw in the api logs. Routing every consumer through this shared query key lets
 * React Query collapse them into a single request. Returns 0 on error/while
 * loading, matching the old `.catch(() => 0)` behaviour.
 */
export function useCredentialErrorCount(orgSlug: string | undefined): number {
  const queryClient = useQueryClient()

  // Other components emit this when they change credentials; refresh the badge.
  // Both sidebar instances add a listener, but invalidateQueries is idempotent
  // and the resulting refetch is itself deduped.
  useEffect(() => {
    const handler = () =>
      queryClient.invalidateQueries({ queryKey: [CREDENTIAL_ERROR_COUNT_KEY] })
    window.addEventListener('credentials-updated', handler)
    return () => window.removeEventListener('credentials-updated', handler)
  }, [queryClient])

  const { data } = useQuery({
    queryKey: [CREDENTIAL_ERROR_COUNT_KEY, orgSlug],
    queryFn: () => integrationsService.getCredentialErrorCount(),
    enabled: !!orgSlug,
    refetchInterval: 10 * 60 * 1000,
    staleTime: 60 * 1000,
  })

  return data ?? 0
}
